import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl'

import { AssessmentStages, AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { getCombinedAssignmentStatus } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { getCHRTHighRiskDetails } from '@lyrahealth-inc/shared-app-logic/src/features/highAlert/utils'
import { type Assignment, AssignmentGroup } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { type Outcome, type QuestionResponse } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import outcomeUtils from '@lyrahealth-inc/ui-core/lib/utils/outcomeUtils'

import {
  type ClinicalMeasureToken,
  getMeasureScoreIds,
  isMeasureVisible,
  type MeasureContext,
  type MeasureDefinition,
  MEASURES,
  resolveMetadataToken,
  type ScoreId,
  SELF_REFLECTION_MEASURE_ID,
  type SupplementalDefinition,
} from './measureCatalog'
import { getAssignmentStage } from '../assessmentModalHelpers'
import { getLocalDateKey, parseOutcomeResponseDate } from './outcomeDate'

/**
 * Builds the rows for the outcome measures table.
 *
 * Two selection strategies, one row builder:
 *  - no date selected -> `latestKnownMeasures`: the most recent score per measure, plus a
 *    pending row for anything an outstanding assignment has asked for but not received.
 *  - a local date selected -> every submission completed that day is projected through
 *    `measuresForOutcome`, grouped by measure, with repeated attempts kept as separate newest-first
 *    rows. No pending rows: the current payload cannot associate an incomplete assignment with a
 *    historical appointment or occurrence.
 *
 * Everything a row needs beyond "which score, from which outcome" is identical between the two,
 * and lives in `toMeasureRow`.
 */

export const EMPTY_VALUE = '–'

const MEASURE_ORDER = new Map<string, number>([
  ...MEASURES.map(({ id }, index) => [id, index] as const),
  [SELF_REFLECTION_MEASURE_ID, MEASURES.length] as const,
])

const MESSAGES = defineMessages({
  pending: {
    defaultMessage: 'Pending',
    description: 'Score shown for an assigned assessment that has not been completed',
  },
  notAvailable: {
    defaultMessage: 'Not available',
    description: 'Accessibility label for an assessment value that is not available',
  },
  therapy: { defaultMessage: 'Therapy', description: 'Therapy program label used in an alliance question' },
  coaching: { defaultMessage: 'Coaching', description: 'Coaching program label used in an alliance question' },
  memberSelfReflection: {
    defaultMessage: 'Member Self Reflection',
    description: 'Name of a member self-reflection response',
  },
  textEntry: { defaultMessage: 'Text entry', description: 'Description shown for a free-text assessment response' },

  // Score severity descriptions.
  outOfRange: { defaultMessage: 'Out of range', description: 'Score outside its supported range' },
  mild: { defaultMessage: 'Mild', description: 'Mild score severity' },
  moderate: { defaultMessage: 'Moderate', description: 'Moderate score severity' },
  severe: { defaultMessage: 'Severe', description: 'Severe score severity' },
  verySevere: { defaultMessage: 'Very Severe', description: 'Very severe score severity' },
  noneToMinimal: { defaultMessage: 'None to minimal', description: 'None-to-minimal score severity' },
  moderateAnxiety: { defaultMessage: 'Moderate Anxiety', description: 'Moderate GAD-7 severity' },
  severeAnxiety: { defaultMessage: 'Severe Anxiety', description: 'Severe GAD-7 severity' },
  moderateDepression: { defaultMessage: 'Moderate Depression', description: 'Moderate PHQ-9 severity' },
  severeDepression: { defaultMessage: 'Severe Depression', description: 'Severe PHQ-9 severity' },
  clinical: { defaultMessage: 'Clinical', description: 'Clinical-range score severity' },
  nonClinical: { defaultMessage: 'Non-clinical', description: 'Non-clinical-range score severity' },
  low: { defaultMessage: 'Low', description: 'Low score severity' },
  high: { defaultMessage: 'High', description: 'High score severity' },
  notDifficultAtAll: { defaultMessage: 'Not difficult at all', description: 'PHQ-9 life impairment: no difficulty' },
  somewhatDifficult: { defaultMessage: 'Somewhat difficult', description: 'PHQ-9 life impairment: some difficulty' },
  veryDifficult: { defaultMessage: 'Very difficult', description: 'PHQ-9 life impairment: high difficulty' },
  extremelyDifficult: {
    defaultMessage: 'Extremely difficult',
    description: 'PHQ-9 life impairment: extreme difficulty',
  },
  notAtAll: { defaultMessage: 'Not at all', description: 'PHQ-9 self-harm: not present' },
  severalDays: { defaultMessage: 'Several days', description: 'PHQ-9 self-harm: several days' },
  moreThanHalfTheDays: {
    defaultMessage: 'More than half the days',
    description: 'PHQ-9 self-harm: more than half the days',
  },
  nearlyEveryDay: { defaultMessage: 'Nearly everyday', description: 'PHQ-9 self-harm: nearly every day' },
  dissatisfied: { defaultMessage: 'Dissatisfied', description: 'Alliance response: dissatisfied' },
  somewhatSatisfied: { defaultMessage: 'Somewhat Satisfied', description: 'Alliance response: somewhat satisfied' },
  satisfied: { defaultMessage: 'Satisfied', description: 'Alliance response: satisfied' },
  verySatisfied: { defaultMessage: 'Very Satisfied', description: 'Alliance response: very satisfied' },
  clinicallyElevated: {
    defaultMessage: 'Clinically Elevated',
    description: 'PCL-5 score at or above the clinical cutoff',
  },
  notClinicallyElevated: {
    defaultMessage: 'Not Clinically Elevated',
    description: 'PCL-5 score below the clinical cutoff',
  },
})

/** Maps the outcome utility's English score descriptions back to localized messages. */
export const SCORE_DESCRIPTION_MESSAGES: Record<string, MessageDescriptor> = {
  'Out of range': MESSAGES.outOfRange,
  Mild: MESSAGES.mild,
  Moderate: MESSAGES.moderate,
  Severe: MESSAGES.severe,
  'Very Severe': MESSAGES.verySevere,
  'None to minimal': MESSAGES.noneToMinimal,
  'Moderate Anxiety': MESSAGES.moderateAnxiety,
  'Severe Anxiety': MESSAGES.severeAnxiety,
  'Moderate Depression': MESSAGES.moderateDepression,
  'Severe Depression': MESSAGES.severeDepression,
  Clinical: MESSAGES.clinical,
  'Non-clinical': MESSAGES.nonClinical,
  Low: MESSAGES.low,
  High: MESSAGES.high,
  'Not difficult at all': MESSAGES.notDifficultAtAll,
  'Somewhat difficult': MESSAGES.somewhatDifficult,
  'Very difficult': MESSAGES.veryDifficult,
  'Extremely difficult': MESSAGES.extremelyDifficult,
  'Not at all': MESSAGES.notAtAll,
  'Several days': MESSAGES.severalDays,
  'More than half the days': MESSAGES.moreThanHalfTheDays,
  'Nearly everyday': MESSAGES.nearlyEveryDay,
  Dissatisfied: MESSAGES.dissatisfied,
  'Somewhat Satisfied': MESSAGES.somewhatSatisfied,
  Satisfied: MESSAGES.satisfied,
  'Very Satisfied': MESSAGES.verySatisfied,
  'Clinically Elevated': MESSAGES.clinicallyElevated,
  'Not Clinically Elevated': MESSAGES.notClinicallyElevated,
}

export type MeasureTooltipDetail = {
  id: string
  label: string
  value: string
}

export type MeasureRow = {
  /** Semantic measure id used for catalog lookup, charts, and score selection. */
  id: string
  /** Concise, unique suffix used by row controls and test ids. */
  testId: string
  /** True when the selected date contains multiple results for this measure. */
  hasDuplicateAttempts: boolean
  measureDisplayName: string
  /** Formatted for display: a number, `n / max`, 'Pending', or an em dash. */
  score: string
  /** Severity band, fixed response-type label, or an em dash. */
  description: string
  supplementalTooltip?: {
    details: MeasureTooltipDetail[]
    /** Drives the warning icon — a critical answer or a positive self-harm response. */
    hasCriticalDetails: boolean
  }
  lastTakenDate?: string
  isPending: boolean
  /** True when an initial screener measure is not part of the first pre-session assessment. */
  isScreenerOnly: boolean
  /**
   * Data the hover actions need. Deliberately not React nodes — the column renders the buttons
   * so the row model stays plain data and testable.
   *
   * `outcome` powers the view-answers modal (its `response_details` filtered by `groups`).
   * `responseDetailsKey` is the assessment type that modal filters by; it defaults to the primary
   * score id but can exist independently for grouped measures such as Treatment Utilization.
   * `rawScore` is the unformatted value the modal displays — `score` above is a presentation
   * string ('14 / 27', 'Pending') and cannot be parsed back.
   * `chartKey` is set only where an OutcomeChart exists for this measure.
   */
  outcome?: Outcome
  scoreId?: string
  responseDetailsKey?: string
  /** Exact response groups to combine when the measure has separately grouped questions. */
  responseDetailsKeys?: readonly string[]
  hideFinalScore?: boolean
  rawScore?: number
  chartKey?: string
  /** Full member-authored reflection shown in the dedicated read-only modal. */
  selfReflectionText?: string
}

export const getAssessmentAnswers = ({
  outcome,
  responseDetailsKey,
  responseDetailsKeys,
}: Pick<MeasureRow, 'outcome' | 'responseDetailsKey' | 'responseDetailsKeys'>): QuestionResponse[] => {
  const keys = responseDetailsKeys ?? (responseDetailsKey ? [responseDetailsKey] : [])
  return outcome?.response_details?.filter(({ groups }) => groups.some((group) => keys.includes(group))) ?? []
}

export const hasAssessmentAnswers = (row: Pick<MeasureRow, 'outcome' | 'responseDetailsKey' | 'responseDetailsKeys'>) =>
  getAssessmentAnswers(row).length > 0

type RowIntl = Pick<IntlShape, 'formatMessage' | 'formatNumber'>

/** Client-assigned assessment statuses that can produce a pending row. */
const OUTSTANDING_STATUSES = new Set<string>([AssignmentStatuses.new, AssignmentStatuses.in_progress])

const isFlexibleAssessmentAssignment = (assignment: Assignment) =>
  getAssignmentStage(assignment) === AssessmentStages.flexible

/** Measure metadata for DA assignment snapshots created before `clinicalMeasuresIncluded` existed. */
const LEGACY_DA_MEASURE_TOKENS_BY_CONTENT_NAME: ReadonlyMap<string, readonly ClinicalMeasureToken[]> = new Map([
  ['daInitialAssessment', ['PHQ', 'GAD', 'PSS', 'WELL-BEING', 'LEAPS']],
  ['daPreSessionAssessment', ['PHQ', 'GAD', 'LEAPS']],
  ['daPreSessionMildAssessment', ['PSS', 'WELL-BEING', 'LEAPS']],
  ['daFinalAssessment', ['PHQ', 'GAD', 'LEAPS']],
  ['daFinalMildAssessment', ['PSS', 'WELL-BEING', 'LEAPS']],
])

const isAvailableScore = (score: unknown): score is number => typeof score === 'number' && Number.isFinite(score)

/**
 * A measure with a `scoreId` needs exactly that score — its supplementals don't count.
 * Measures that have only supplemental scores (Alliance and Treatment Utilization) qualify from any one.
 */
const hasQualifyingScore = (measure: MeasureDefinition, outcome: Outcome) => {
  const scoreIds: readonly ScoreId[] = measure.scoreId ? [measure.scoreId] : getMeasureScoreIds(measure)
  return scoreIds.some((scoreId) => isAvailableScore(outcome.scores?.[scoreId]))
}

const getResponseTime = (outcome: Outcome) => {
  return parseOutcomeResponseDate(outcome.response_date)?.getTime()
}

/** Newest first. */
const sortedSubmittedOutcomes = (outcomes: readonly Outcome[] = []) =>
  outcomes
    .filter((outcome) => getResponseTime(outcome) !== undefined)
    .sort((left, right) => (getResponseTime(right) ?? 0) - (getResponseTime(left) ?? 0))

/** Identifies a submission without depending on its current position in the outcomes array. */
const getOutcomeKey = (outcome: Outcome) => {
  const responseDate = parseOutcomeResponseDate(outcome.response_date)?.toISOString() ?? outcome.response_date

  return JSON.stringify([
    outcome.id ?? null,
    outcome.outcome_token ?? null,
    outcome.assignment_id ?? null,
    responseDate,
    outcome.date_created ?? null,
    outcome.type ?? null,
  ])
}

export type SelectableOutcome = {
  key: string
  outcome: Outcome
}

export type OutcomeDateBucket = {
  /** Browser-local calendar date in YYYY-MM-DD form. */
  key: string
  /** Newest response instant in this bucket, used to format the dropdown label. */
  date: Date
  /** Exact submissions on this date, newest first. */
  submissions: SelectableOutcome[]
}

/**
 * The submitted outcomes a provider can select between, newest first.
 *
 * Outcome ids can be missing or reused, so the key combines the existing submission fields.
 * A count only disambiguates otherwise identical records; inserting a distinct outcome does not
 * renumber the existing options.
 */
export const getSelectableOutcomes = (outcomes: readonly Outcome[] = []): SelectableOutcome[] => {
  const occurrences = new Map<string, number>()
  return sortedSubmittedOutcomes(outcomes).map((outcome) => {
    const baseKey = getOutcomeKey(outcome)
    const occurrence = occurrences.get(baseKey) ?? 0
    occurrences.set(baseKey, occurrence + 1)
    return { key: `${baseKey}-${occurrence}`, outcome }
  })
}

/** One selectable option per browser-local calendar date, newest first. */
export const getSelectableOutcomeDates = (outcomes: readonly Outcome[] = []): OutcomeDateBucket[] => {
  const buckets = new Map<string, OutcomeDateBucket>()

  // getSelectableOutcomes is newest-first, and Map preserves insertion order, so both the date
  // buckets and each bucket's submissions remain newest-first without another sort.
  for (const submission of getSelectableOutcomes(outcomes)) {
    const responseDate = parseOutcomeResponseDate(submission.outcome.response_date)
    if (!responseDate) continue

    const key = getLocalDateKey(responseDate)
    const existingBucket = buckets.get(key)
    if (existingBucket) {
      existingBucket.submissions.push(submission)
    } else {
      buckets.set(key, { key, date: responseDate, submissions: [submission] })
    }
  }

  return [...buckets.values()]
}

export const findOutcomeDateByKey = (outcomes: readonly Outcome[] = [], key: string | undefined) =>
  key === undefined ? undefined : getSelectableOutcomeDates(outcomes).find((bucket) => bucket.key === key)

const describeScore = (scoreId: string, score: number, { formatMessage }: RowIntl) => {
  const description = outcomeUtils.convertOutcomeScoreToDescription(scoreId, score)
  const message = SCORE_DESCRIPTION_MESSAGES[description]
  return message ? formatMessage(message) : ''
}

const getProgramLabel = (outcome: Outcome | undefined, { formatMessage }: RowIntl) =>
  formatMessage(outcome?.program_name?.toLowerCase().includes('coaching') ? MESSAGES.coaching : MESSAGES.therapy)

const formatSupplementalValue = (definition: SupplementalDefinition, score: number, intl: RowIntl): string => {
  const { formatNumber } = intl
  if (definition.format === 'description') {
    return describeScore(definition.descriptionScoreId ?? definition.id, score, intl) || EMPTY_VALUE
  }
  if (definition.format === 'percent') {
    return formatNumber(score / 100, { style: 'percent', maximumFractionDigits: 2 })
  }
  return definition.maxScore == null
    ? formatNumber(score)
    : `${formatNumber(score)} / ${formatNumber(definition.maxScore)}`
}

type SupplementalResult = { definition: SupplementalDefinition; score: number }

const getSupplementalResults = (measure: MeasureDefinition, outcome: Outcome): SupplementalResult[] =>
  (measure.supplemental ?? []).flatMap((definition) => {
    const score = outcome.scores?.[definition.id]
    if (!isAvailableScore(score)) return []
    if (definition.omitWhenZero && score <= 0) return []
    return [{ definition, score }]
  })

/**
 * The one row builder. Both selection strategies funnel through here so display name, score
 * formatting, description, tooltip and hover-action data can only be derived one way.
 */
const toMeasureRow = (
  measure: MeasureDefinition,
  { outcome, assignment, isPending = false }: { outcome?: Outcome; assignment?: Assignment; isPending?: boolean },
  intl: RowIntl,
): MeasureRow => {
  const { formatMessage, formatNumber } = intl
  const primaryScore = measure.scoreId && outcome ? outcome.scores?.[measure.scoreId] : undefined
  const hasPrimaryScore = isAvailableScore(primaryScore)
  const supplementalResults = outcome ? getSupplementalResults(measure, outcome) : []
  const program = getProgramLabel(outcome, intl)

  const criticalResponses: QuestionResponse[] =
    measure.hasCriticalResponses && outcome ? getCHRTHighRiskDetails(outcome.response_details) : []

  const details: MeasureTooltipDetail[] = [
    ...supplementalResults.map(({ definition, score }) => ({
      id: definition.id,
      label: formatMessage(definition.label, { program }),
      value: formatSupplementalValue(definition, score, intl),
    })),
    ...criticalResponses.map((response) => ({
      id: `${measure.id}-${response['question-number']}`,
      label: response.question,
      value: response['answer-text'],
    })),
  ]

  const score = isPending
    ? formatMessage(MESSAGES.pending)
    : hasPrimaryScore
      ? measure.maxScore != null && primaryScore <= measure.maxScore
        ? `${formatNumber(primaryScore)} / ${formatNumber(measure.maxScore)}`
        : formatNumber(primaryScore)
      : EMPTY_VALUE

  // Measures with no primary score (Alliance and Treatment Utilization) describe themselves
  // with the mean of their parts, where those parts share a band table.
  const fallbackDescription = supplementalResults.find(({ definition }) => definition.descriptionScoreId)
  const supplementalMean =
    supplementalResults.reduce((total, { score }) => total + score, 0) / supplementalResults.length
  const description =
    hasPrimaryScore && measure.scoreId
      ? describeScore(measure.scoreId, primaryScore, intl) || EMPTY_VALUE
      : fallbackDescription
        ? formatSupplementalValue(fallbackDescription.definition, supplementalMean, intl)
        : EMPTY_VALUE

  return {
    id: measure.id,
    testId: measure.id,
    hasDuplicateAttempts: false,
    measureDisplayName: formatMessage(measure.name),
    score,
    description,
    supplementalTooltip: details.length
      ? {
          details,
          hasCriticalDetails:
            criticalResponses.length > 0 || supplementalResults.some(({ definition }) => definition.omitWhenZero),
        }
      : undefined,
    lastTakenDate: isPending ? undefined : (getLatestSubmitDate(assignment) ?? outcome?.response_date),
    isPending,
    isScreenerOnly: false,
    outcome: isPending ? undefined : outcome,
    scoreId: measure.scoreId,
    responseDetailsKey: measure.responseDetailsKey ?? measure.scoreId,
    responseDetailsKeys: measure.responseDetailsKeys,
    hideFinalScore: measure.hideFinalScore,
    rawScore: hasPrimaryScore ? primaryScore : undefined,
    chartKey: measure.chartKey,
  }
}

const resolveMeasureTokens = (tokens: Iterable<string>): MeasureDefinition[] => {
  const measures = new Map<string, MeasureDefinition>()
  for (const token of tokens) {
    for (const measure of resolveMetadataToken(token)) measures.set(measure.id, measure)
  }
  return [...measures.values()]
}

/** Measures an assignment is asking the client to complete. */
const getAssignmentMeasures = (assignment: Assignment): MeasureDefinition[] => {
  const topLevelTokens = assignment.content_meta_data?.clinicalMeasuresIncluded
  const nestedTokens = assignment.content.meta_data?.clinicalMeasuresIncluded

  // Both metadata shapes appear in the wild. Explicit metadata, including an empty list, wins;
  // only older DA snapshots without either field use the content-name fallback.
  const hasExplicitMetadata = topLevelTokens !== undefined || nestedTokens !== undefined
  const tokens = hasExplicitMetadata
    ? [...(topLevelTokens ?? []), ...(nestedTokens ?? [])]
    : (LEGACY_DA_MEASURE_TOKENS_BY_CONTENT_NAME.get(assignment.content.name) ?? [])

  return resolveMeasureTokens(tokens)
}

/** Order by session period ascending, falling back to creation date then ID for deterministic tie breaking. */
const compareAssignmentOrder = (left: Assignment, right: Assignment) => {
  const leftSessionPeriod = Number.isFinite(left.session_period) ? left.session_period : undefined
  const rightSessionPeriod = Number.isFinite(right.session_period) ? right.session_period : undefined
  if (leftSessionPeriod !== undefined && rightSessionPeriod !== undefined) {
    const sessionPeriodDifference = leftSessionPeriod - rightSessionPeriod
    if (sessionPeriodDifference !== 0) return sessionPeriodDifference
  } else if (leftSessionPeriod !== undefined) return -1
  else if (rightSessionPeriod !== undefined) return 1

  const leftCreateTime = parseOutcomeResponseDate(left.create_date)?.getTime()
  const rightCreateTime = parseOutcomeResponseDate(right.create_date)?.getTime()
  if (leftCreateTime !== undefined && rightCreateTime !== undefined) {
    const createTimeDifference = leftCreateTime - rightCreateTime
    if (createTimeDifference !== 0) return createTimeDifference
  } else if (leftCreateTime !== undefined) return -1
  else if (rightCreateTime !== undefined) return 1

  return left.id.localeCompare(right.id)
}

/**
 * Earliest instant a response to this assignment was submitted, or undefined if none has been.
 *
 * `submit_date` is stamped server-side the moment a response is scored, the same request that
 * moves the episode off its `unset` track, so it stands in for "the track is now known" without
 * depending on assignment status or ordering of `assignment_responses`.
 */
const getEarliestSubmitTime = (assignment: Assignment): number | undefined => {
  const submitTimes = (assignment.assignment_responses ?? []).flatMap((response) => {
    const submitTime = parseOutcomeResponseDate(response.submit_date)?.getTime()
    return submitTime !== undefined ? [submitTime] : []
  })
  return submitTimes.length > 0 ? Math.min(...submitTimes) : undefined
}

/**
 * Measures that appeared on the screener but are not part of the client's current ongoing
 * assessments. Compares the initial assessment against the latest pre-session assignment to
 * reflect the current track, even if it changed mid-episode.
 *
 * Only pre-session assignments created after the screener was submitted are eligible: DATS keeps
 * assigning the pre-`unset` content (same measures as the still-unscored screener, so it's already
 * excluded by the content-name check) until the episode's track is set, so an earlier
 * differently-named pre-session would predate the track change entirely. Until DATS has created
 * one of those, there's no source of truth for what's ongoing, so nothing is tagged.
 */
const getScreenerOnlyMeasureIds = (assignments: readonly Assignment[] = []): ReadonlySet<string> => {
  const assessmentAssignments = assignments.filter(({ content }) => content.group === AssignmentGroup.ASSESSMENT)
  const initialSnapshots = assessmentAssignments.flatMap((assignment) => {
    if (getAssignmentStage(assignment) !== AssessmentStages.initial) return []
    const measureIds = getAssignmentMeasures(assignment).map(({ id }) => id)
    return measureIds.length > 0 ? [{ assignment, measureIds }] : []
  })
  const initialSnapshot = initialSnapshots.sort((left, right) =>
    compareAssignmentOrder(left.assignment, right.assignment),
  )[0]
  if (!initialSnapshot) return new Set()

  const initialSubmitTime = getEarliestSubmitTime(initialSnapshot.assignment)
  if (initialSubmitTime === undefined) return new Set()

  const preSessionAssignments = assessmentAssignments
    .filter((assignment) => {
      if (getAssignmentStage(assignment) !== AssessmentStages.pre_session) return false
      if (assignment.content.name === initialSnapshot.assignment.content.name) return false
      const createTime = parseOutcomeResponseDate(assignment.create_date)?.getTime()
      return createTime !== undefined && createTime > initialSubmitTime
    })
    .sort(compareAssignmentOrder)
  const latestPreSessionAssignment = preSessionAssignments[preSessionAssignments.length - 1]
  if (!latestPreSessionAssignment) return new Set()

  const initialMeasureIds = new Set(initialSnapshot.measureIds)
  const continuingMeasureIds = new Set(getAssignmentMeasures(latestPreSessionAssignment).map(({ id }) => id))
  if (continuingMeasureIds.size === 0) return new Set()

  return new Set([...initialMeasureIds].filter((id) => !continuingMeasureIds.has(id)))
}

const getPendingMeasureIds = (assignments: readonly Assignment[] = []): Set<string> => {
  const pending = new Set<string>()
  for (const assignment of assignments) {
    if (assignment.content.group !== AssignmentGroup.ASSESSMENT) continue
    const status = getCombinedAssignmentStatus({ assignment })
    const isClientDraft = status === AssignmentStatuses.draft && OUTSTANDING_STATUSES.has(assignment.status)
    if (!OUTSTANDING_STATUSES.has(status) && !isClientDraft) continue
    for (const measure of getAssignmentMeasures(assignment)) pending.add(measure.id)
  }
  return pending
}

/**
 * The outcome holding this measure's most recent score.
 *
 * Searched per measure across every outcome, so each row can come from a different submission —
 * a PHQ answered last week and a WSAS answered three months ago both show their own latest value.
 *
 * The search keys on the measure's *primary* score, not on any score it reads. Keying on any
 * would let a submission that carried only PHQ-LIFE win over an older one that carried the actual
 * PHQ score, and the row would show an em dash while a real score existed. Measures with no
 * primary score of their own (Alliance and Treatment Utilization) fall back to their parts.
 *
 * Once chosen, every value on the row comes from that one outcome, so the score, its supplemental
 * details and `lastTakenDate` always describe the same submission.
 *
 * Visibility is checked after selection so a hidden newer Alliance result cannot be replaced by
 * an older result. This matches the legacy owner-or-supervisor rule for the latest result.
 */
const findLatestOutcomeFor = (measure: MeasureDefinition, sortedOutcomes: readonly Outcome[]) =>
  sortedOutcomes.find((outcome) => hasQualifyingScore(measure, outcome))

const findAssignmentForOutcome = (outcome: Outcome | undefined, assignments: readonly Assignment[] = []) =>
  outcome?.assignment_id ? assignments.find(({ id }) => id === outcome.assignment_id) : undefined

export const getLatestSubmitDate = (assignment?: Assignment): string | undefined =>
  (assignment?.assignment_responses ?? []).reduce<{ date: string; time: number } | undefined>((latest, response) => {
    const submitDate = response.submit_date
    const submitTime = parseOutcomeResponseDate(submitDate)?.getTime()
    if (!submitDate || submitTime === undefined) return latest
    return latest === undefined || submitTime > latest.time ? { date: submitDate, time: submitTime } : latest
  }, undefined)?.date

const getSelfReflection = (outcome: Outcome | undefined, assignments: readonly Assignment[] = []) => {
  const text = findAssignmentForOutcome(outcome, assignments)?.assignment_responses?.[0]?.response?.self_reflection
  return typeof text === 'string' && text ? text : undefined
}

const toSelfReflectionRow = (
  outcome: Outcome,
  assignment: Assignment | undefined,
  text: string,
  { formatMessage }: RowIntl,
): MeasureRow => ({
  id: SELF_REFLECTION_MEASURE_ID,
  testId: SELF_REFLECTION_MEASURE_ID,
  hasDuplicateAttempts: false,
  measureDisplayName: formatMessage(MESSAGES.memberSelfReflection),
  score: EMPTY_VALUE,
  description: formatMessage(MESSAGES.textEntry),
  lastTakenDate: getLatestSubmitDate(assignment) ?? outcome.response_date,
  isPending: false,
  isScreenerOnly: false,
  selfReflectionText: text,
})

export const latestKnownMeasures = (
  outcomes: readonly Outcome[] = [],
  assignments: readonly Assignment[] = [],
  allAssignments: readonly Assignment[] = assignments,
  context: MeasureContext = {},
  intl: RowIntl,
): MeasureRow[] => {
  const sortedOutcomes = sortedSubmittedOutcomes(outcomes)
  const pendingMeasureIds = getPendingMeasureIds(assignments)
  const screenerOnlyMeasureIds = getScreenerOnlyMeasureIds(assignments)

  const measureRows = MEASURES.flatMap<MeasureRow>((measure) => {
    const outcome = findLatestOutcomeFor(measure, sortedOutcomes)
    if (outcome) {
      return isMeasureVisible(measure, context, outcome)
        ? [
            {
              ...toMeasureRow(
                measure,
                { outcome, assignment: findAssignmentForOutcome(outcome, allAssignments) },
                intl,
              ),
              isScreenerOnly: screenerOnlyMeasureIds.has(measure.id),
            },
          ]
        : []
    }
    return isMeasureVisible(measure, context) && pendingMeasureIds.has(measure.id)
      ? [toMeasureRow(measure, { isPending: true }, intl)]
      : []
  })

  const reflectionOutcome = sortedOutcomes.find((outcome) => getSelfReflection(outcome, allAssignments))
  const reflectionText = getSelfReflection(reflectionOutcome, allAssignments)
  if (reflectionOutcome && reflectionText)
    measureRows.push(
      toSelfReflectionRow(
        reflectionOutcome,
        findAssignmentForOutcome(reflectionOutcome, allAssignments),
        reflectionText,
        intl,
      ),
    )

  return measureRows
}

export const measuresForOutcome = (
  outcome: Outcome | undefined,
  allAssignments: readonly Assignment[] = [],
  context: MeasureContext = {},
  intl: RowIntl,
): MeasureRow[] => {
  if (!outcome) return []

  const assignment = findAssignmentForOutcome(outcome, allAssignments)
  const measureRows = MEASURES.flatMap<MeasureRow>((measure) => {
    if (!isMeasureVisible(measure, context, outcome)) return []
    return hasQualifyingScore(measure, outcome) ? [toMeasureRow(measure, { outcome, assignment }, intl)] : []
  })

  const reflectionText = getSelfReflection(outcome, allAssignments)
  if (reflectionText) measureRows.push(toSelfReflectionRow(outcome, assignment, reflectionText, intl))

  return measureRows
}

export type GetMeasureRowsOptions = {
  /** Browser-local date key from `getSelectableOutcomeDates`. Undefined means "last known measures". */
  selectedDateKey?: string
  outcomes?: readonly Outcome[]
  /** Scoped to the selected episode and recipient — drives which measures show as pending. */
  assignments?: readonly Assignment[]
  /**
   * Every assignment for the client, used to resolve self reflections and submit dates.
   *
   * Deliberately not episode-scoped: both are found by exact `outcome.assignment_id` match, and
   * the outcome is already in scope, so scoping again adds nothing and silently drops the match
   * whenever the assignment's `episode_id` is absent or disagrees.
   *
   * Defaults to `assignments`.
   */
  selfReflectionAssignments?: readonly Assignment[]
  context?: MeasureContext
  intl: RowIntl
}

export const getMeasureRows = ({
  selectedDateKey,
  outcomes = [],
  assignments = [],
  selfReflectionAssignments,
  context = {},
  intl,
}: GetMeasureRowsOptions): MeasureRow[] => {
  const reflectionAssignments = selfReflectionAssignments ?? assignments
  const flexibleAssignmentIds = new Set(
    assignments.filter(isFlexibleAssessmentAssignment).map((assignment) => assignment.id),
  )
  // An outcome follows the classification of its exact linked assignment. Outcomes without an
  // assignment id cannot be classified as flexible, so they remain in Automated assessments.
  const isExcludedOutcome = (outcome: Outcome) =>
    outcome.assignment_id ? flexibleAssignmentIds.has(outcome.assignment_id) : false

  if (selectedDateKey !== undefined) {
    const selectedDate = findOutcomeDateByKey(outcomes, selectedDateKey)
    const rowsByMeasure = new Map<string, MeasureRow[]>()

    for (const { outcome } of selectedDate?.submissions ?? []) {
      if (isExcludedOutcome(outcome)) continue

      for (const row of measuresForOutcome(outcome, reflectionAssignments, context, intl)) {
        const rows = rowsByMeasure.get(row.id)
        if (rows) rows.push(row)
        else rowsByMeasure.set(row.id, [row])
      }
    }

    return [...rowsByMeasure.entries()]
      .sort(
        ([leftId], [rightId]) =>
          (MEASURE_ORDER.get(leftId) ?? Number.MAX_SAFE_INTEGER) -
          (MEASURE_ORDER.get(rightId) ?? Number.MAX_SAFE_INTEGER),
      )
      .flatMap(([measureId, entries]) =>
        entries.map((row, index) => ({
          ...row,
          testId: entries.length > 1 ? `${measureId}-attempt-${index + 1}` : measureId,
          hasDuplicateAttempts: entries.length > 1,
        })),
      )
  }

  const automatedAssignments = assignments.filter((assignment) => !flexibleAssignmentIds.has(assignment.id))
  const automatedOutcomes = outcomes.filter((outcome) => !isExcludedOutcome(outcome))
  return latestKnownMeasures(automatedOutcomes, automatedAssignments, reflectionAssignments, context, intl)
}
